import { message } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useUser } from 'reducers';
import { OrderType, PaymentType, TaskViewClassic, TaskViewLoose } from 'types';
import API from '../../API';

export enum DriverCardEventType {
	classicOrder,
	looseOrder,
	expense,
	settlement,
}

export type DriverCardEvent =
	| {
			type: DriverCardEventType.classicOrder;
			key: string;
			date: moment.Moment;
			task: TaskViewClassic;
	  }
	| {
			type: DriverCardEventType.looseOrder;
			key: string;
			date: moment.Moment;
			task: TaskViewLoose;
	  }
	| {
			type: DriverCardEventType.expense;
			key: string;
			date: moment.Moment;
			expense: any;
	  }
	| {
			type: DriverCardEventType.settlement;
			key: string;
			date: moment.Moment;
			settlement: any;
	  };

export interface DriverCardSummary {
	payments: number;
	advances: number;
	spent: number;
	settlements: number;
}

export const useDriverCard = (driverId: number, daysOffset: number) => {
	const [driver] = useUser(driverId);
	const [events, setEvents] = useState<DriverCardEvent[]>();
	const [summary, setSummary] = useState<DriverCardSummary>();

	useEffect(() => {
		fetchEvents(driverId, daysOffset);
	}, [driverId, daysOffset]);

	const getPayments = (after: moment.Moment, before: moment.Moment, events: DriverCardEvent[]) => {
		const classicPayments = events
			.filter((e) => e.type === DriverCardEventType.classicOrder)
			.reduce((acc, e) => {
				if (!moment(e.task.order.paymentDate).isBetween(after, before)) {
					return acc;
				}

				if (!e.task.order.paymentDate) {
					return acc;
				}

				if (e.task.order.paymentBy !== driverId) {
					return acc;
				}

				if (e.task.order.paymentType !== PaymentType.cash) {
					return acc;
				}

				return acc + e.task.order.grossPrice;
			}, 0);

		const loosePayments = events
			.filter((e) => e.type === DriverCardEventType.looseOrder)
			.reduce((acc, e) => {
				const payments = e.task.order.payments.filter(
					(p) => moment(p.date).isBetween(after, before) && p.type === PaymentType.cash && p.createdBy === driverId
				);

				return acc + payments.reduce((acc, p) => acc + p.amount, 0);
			}, 0);

		return classicPayments + loosePayments;
	};

	const getAdvances = (events: DriverCardEvent[]) =>
		events
			.filter((e) => e.type === DriverCardEventType.classicOrder)
			.reduce((acc, e) => acc + (e.task.order.advance ?? 0), 0);

	const getSpent = (events: DriverCardEvent[]) =>
		events.filter((e) => e.type === DriverCardEventType.expense).reduce((acc, e) => acc + e.expense.value, 0);

	const getSettlements = (events: DriverCardEvent[]) =>
		events.filter((e) => e.type === DriverCardEventType.settlement).reduce((acc, e) => acc + e.settlement.value, 0);

	const fetchEvents = async (driverId: number, daysOffset: number) => {
		try {
			const after = moment().add(daysOffset, 'days').startOf('day');
			const before = moment().add(daysOffset, 'days').endOf('day');

			const [tasks, expenses, settlements] = await Promise.all([
				API.tasks.getAll({
					after,
					before,
					assignee: driverId,
				}),
				API.expenses.getAll({
					user_id: driverId,
					after: after.unix() * 1000,
					before: before.unix() * 1000,
					company: undefined,
				}) as Promise<any[]>,
				API.expenses.settlements.getAll({
					user_id: driverId,
					after: after.unix() * 1000,
					before: before.unix() * 1000,
				}) as Promise<any[]>,
			]);

			const events: DriverCardEvent[] = [];

			for (const task of tasks) {
				if (task.orderType === OrderType.classic) {
					events.push({
						type: DriverCardEventType.classicOrder,
						key: `classic-${task.type}-${task.id}`,
						date: moment(task.doneAt),
						task,
					});
				}

				if (task.orderType === OrderType.loose) {
					events.push({
						type: DriverCardEventType.looseOrder,
						key: `loose-${task.type}-${task.id}`,
						date: moment(task.doneAt),
						task,
					});
				}
			}

			for (const expense of expenses) {
				events.push({
					type: DriverCardEventType.expense,
					key: `expense-${expense.id}`,
					date: moment(expense.created),
					expense,
				});
			}

			for (const settlement of settlements) {
				events.push({
					type: DriverCardEventType.settlement,
					key: `settlement-${settlement.id}`,
					date: moment(settlement.created),
					settlement,
				});
			}

			events.sort((a, b) => a.date.unix() - b.date.unix());

			setEvents(events);
			setSummary({
				payments: getPayments(after, before, events),
				advances: getAdvances(events),
				spent: getSpent(events),
				settlements: getSettlements(events),
			});
		} catch (err) {
			console.error(err);
			message.error(`Nie udało się pobrać danych (${err})`);
		}
	};

	return {
		driver,
		events,
		summary,
	};
};
